import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@ibsys/kit';
import { AlertItem, type HiddenCriterionRef, type IndicatorSelectHandler } from '@/entities/dashboards';
import { DASHBOARD_BLOCK_SCROLL_CLASS, cn, preventShiftSelection, zoneCardClass } from '@/shared/lib';
import { isCriterionHidden } from '../model/hiddenCriteria';
import { DASHBOARD_CLICKABLE_BLOCK_CLASS } from './utils';

type Props = {
  alerts: AlertItem[];
  /** Критерии, скрытые пользователем (каждый — в разрезе своего заключения). */
  hiddenCriteria: HiddenCriterionRef[];
  /** Переход к диалогам показателя, по которому сработал алерт */
  onSelectAlert: IndicatorSelectHandler;
};

/**
 * Карточка «Красные зоны»: список алертов с цветовой индикацией критичности.
 * 'red' — красный фон, 'yellow' — жёлтый.
 *
 * Алерт по критерию, снятому в настройках отображения, не показывается; алерты по консультантам
 * и по не снятым критериям остаются. Критерий опознаётся вместе со своим заключением, поэтому
 * снятие галочки убирает алерт только в этом заключении, а одноимённый критерий соседнего
 * заключения остаётся на месте.
 *
 * Клик по алерту открывает диалоги показателя, по которому он сработал. Алерт без диалогов
 * разбирать нечего, поэтому переход по нему не предлагается.
 */
export const AlertsCard: FC<Props> = ({ alerts, hiddenCriteria, onSelectAlert }) => {
  const { t } = useTranslation('qualityDashboard');

  const visibleAlerts = useMemo(
    () =>
      alerts.filter(
        (alert) =>
          !(
            alert.subject === 'criterion' &&
            alert.expertReportId !== null &&
            isCriterionHidden(hiddenCriteria, alert.expertReportId, alert.name)
          ),
      ),
    [alerts, hiddenCriteria],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('redZones')}</CardTitle>
      </CardHeader>
      <CardContent>
        {visibleAlerts.length === 0 ? (
          <div className="text-muted-foreground text-sm">{t('noAlerts')}</div>
        ) : (
          <div className={`${DASHBOARD_BLOCK_SCROLL_CLASS} flex flex-col gap-2`}>
            {visibleAlerts.map((alert, index) => {
              const isClickable = !!alert.selection;
              const className = cn('rounded-lg border p-3 text-sm', zoneCardClass[alert.type]);

              if (!isClickable) {
                return (
                  <div key={index} className={className}>
                    {alert.text}
                  </div>
                );
              }

              return (
                <div
                  key={index}
                  role="button"
                  tabIndex={-1}
                  aria-label={t('alerts.openDialogs')}
                  className={cn(className, DASHBOARD_CLICKABLE_BLOCK_CLASS)}
                  onMouseDown={preventShiftSelection}
                  onClick={(event) =>
                    onSelectAlert({ label: alert.text, selection: alert.selection }, event.shiftKey)
                  }
                >
                  {alert.text}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
